import { useState, useEffect, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import ShareLectureDialog from '../components/ShareLectureDialog';
import SubjectSelectWithInlineCreate from '../components/SubjectSelectWithInlineCreate';
import { useRecording } from '../contexts/RecordingContext';
import { useTranscription } from '../contexts/TranscriptionContext';
import { 
  Plus, Trash2, Mic, GraduationCap, Sparkles, Play, Pause, 
  Share2, Lightbulb, Clock, Zap, BookOpen, Loader2, Volume2, Radio, AlertCircle,
  Download, HelpCircle, X, Bookmark, Shield, Cloud, Cpu, Upload, FileText, Minimize2, ChevronDown, Link2,
  Wifi, WifiOff, Check
} from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { format } from 'date-fns';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import { ImportModal } from '../components/ImportModal';
import { LectureCard } from '../components/lectures/LectureCard';
import Tip from '../components/Tip';
import PageSkeleton from '../components/PageSkeleton';
import EmptyState from '../components/EmptyState';
import AudioUploadDialog from '../components/AudioUploadDialog';
import CreateLectureForm from '../components/lectures/CreateLectureForm';
import LiveRecordingDialog from '../components/lectures/LiveRecordingDialog';
import useLiveRecording from '../components/lectures/useLiveRecording';
import { useCelebration } from '../contexts/CelebrationContext';
import { useSounds } from '../hooks/useSounds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const IMPORT_SOURCE_ICONS = {
  youtube_import: '▶',
  image_ocr: '📷',
  slides_import: '📑',
};
const getImportIcon = (src) => IMPORT_SOURCE_ICONS[src] || '🎵';

const LecturesPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { checkAndCelebrate } = useCelebration();
  const { play: playSound } = useSounds();
  // Recording state from global context
  const {
    recording,
    transcribing,
    recordingTime,
    liveTranscript,
    interimText,
    speechSupported,
    isListening,
    confusedMarkers,
    bookmarks,
    lastVoiceCommand,
    audioChunks,
    isRecordingDialogOpen,
    recordingSource,
    startRecording,
    startTabCapture,
    stopRecording: contextStopRecording,
    markConfused,
    addBookmark,
    pauseSpeechRecognition,
    resumeSpeechRecognition,
    setLiveTranscript,
    setIsRecordingDialogOpen,
    formatTime,
  } = useRecording();

  const {
    mode: transcriptionMode,
    setMode: setTranscriptionMode,
    tier,
    localModelReady,
    localModelLoading,
    localModelProgress,
    localModelFailed,
    initLocalWhisper,
  } = useTranscription();

  const [lectures, setLectures] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({ subject_id: '', title: '', topic: '', notes: '', transcript: '', is_public: false });
  const [autoGenerate, setAutoGenerate] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [selectedLecture, setSelectedLecture] = useState(null);
  const [shareOpen, setShareOpen] = useState(false);
  const [shareLecture, setShareLecture] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(searchParams.get('subject') || 'all');
  const [liveRecordingOpen, setLiveRecordingOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  
  // Post-recording assets — consolidated into useLiveRecording() hook.
  // Single hook call replaces 7 useState/2 useRef declarations and gives
  // us a reusable reset() that the parent's save-success handler calls
  // to clear all post-recording state in one shot.
  const rec = useLiveRecording();
  const {
    recordedAudioUrl, setRecordedAudioUrl,
    recordedAudioBlob, setRecordedAudioBlob,
    finalTranscriptText, setFinalTranscriptText,
    attachedFiles, setAttachedFiles,
    attachUploading, setAttachUploading,
    attachFileRef,
    titleSuggestions, setTitleSuggestions,
    suggestingTitle, setSuggestingTitle,
    transcriptContainerRef,
    reset: resetRecordingState,
  } = rec;

  const suggestTitles = async () => {
    const transcript = (finalTranscriptText || formData.transcript || '').trim();
    if (transcript.length < 20) {
      toast.info('Need a bit more transcript to suggest a title');
      return;
    }
    setSuggestingTitle(true);
    try {
      const subject = subjects.find((s) => s.id === formData.subject_id);
      const { data } = await axios.post(`${API}/lectures/suggest-title`, {
        transcript,
        subject_name: subject?.name,
      });
      const titles = Array.isArray(data?.titles) ? data.titles.slice(0, 3) : [];
      if (titles.length === 0) {
        toast.error('Could not generate titles');
        return;
      }
      setTitleSuggestions(titles);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not suggest titles');
    } finally {
      setSuggestingTitle(false);
    }
  };

  // Sync dialog state with RecordingContext so FloatingRecorder knows when to show
  useEffect(() => {
    setIsRecordingDialogOpen(liveRecordingOpen);
  }, [liveRecordingOpen, setIsRecordingDialogOpen]);

  // Listen for FloatingRecorder expand request
  useEffect(() => {
    const handleOpen = () => setLiveRecordingOpen(true);
    window.addEventListener('openRecordingDialog', handleOpen);
    return () => window.removeEventListener('openRecordingDialog', handleOpen);
  }, []);

  // Minimize recording: close dialog but keep recording
  const minimizeRecording = () => {
    setLiveRecordingOpen(false);
  };

  // Swipe down gesture for recording modal
  const touchStartY = useRef(null);
  const handleRecordingTouchStart = (e) => {
    touchStartY.current = e.touches[0].clientY;
  };
  const handleRecordingTouchEnd = (e) => {
    if (touchStartY.current === null) return;
    const deltaY = e.changedTouches[0].clientY - touchStartY.current;
    if (deltaY > 80 && recording) {
      if (navigator.vibrate) navigator.vibrate(20);
      minimizeRecording();
      toast.info('Recording minimized — tap the floating widget to resume', { duration: 2000 });
    }
    touchStartY.current = null;
  };
  
  // "I'm Confused" button states
  const [confusedPopupOpen, setConfusedPopupOpen] = useState(false);
  const [confusedExplanation, setConfusedExplanation] = useState('');
  const [confusedLoading, setConfusedLoading] = useState(false);
  const [docUploading, setDocUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchData();
  }, []);

  useEffect(() => {
    // Auto-scroll transcript
    if (transcriptContainerRef.current) {
      transcriptContainerRef.current.scrollTop = transcriptContainerRef.current.scrollHeight;
    }
    // Ref objects are stable; ESLint can't see through the hook.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [liveTranscript, interimText]);

  // Open recording modal automatically if we navigate here while recording
  useEffect(() => {
    if (recording && !liveRecordingOpen) {
      setLiveRecordingOpen(true);
    }
  }, [recording, liveRecordingOpen]);

  // Deep-link: ?autorecord=1 (from class-reminder push notification) opens
  // the recording modal immediately so the user can tap once and start.
  // We DO NOT auto-press record — mic permission must be a deliberate
  // user gesture or the WebView will silently deny.
  useEffect(() => {
    if (searchParams.get('autorecord') === '1' && !recording && !liveRecordingOpen) {
      setLiveRecordingOpen(true);
      // Clean the param so a refresh doesn't re-open.
      const next = new URLSearchParams(searchParams);
      next.delete('autorecord');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    try {
      const [lecturesRes, subjectsRes] = await Promise.all([
        axios.get(`${API}/lectures`),
        axios.get(`${API}/subjects`)
      ]);
      setLectures(lecturesRes.data);
      setSubjects(subjectsRes.data);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleDocumentUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setDocUploading(true);
    setUploadProgress(0);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const res = await axios.post(`${API}/uploads/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (progressEvent) => {
          const pct = Math.round((progressEvent.loaded * 100) / (progressEvent.total || 1));
          setUploadProgress(pct);
        },
      });
      toast.success(`"${res.data.filename}" uploaded! Go to Document Uploads to analyze it.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally {
      setDocUploading(false);
      setUploadProgress(0);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleAttachFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAttachUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await axios.post(`${API}/uploads/upload`, fd, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setAttachedFiles(prev => [...prev, { name: file.name, size: file.size, id: res.data.id || res.data.filename }]);
      toast.success(`"${file.name}" attached`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Attach failed');
    } finally {
      setAttachUploading(false);
      if (attachFileRef.current) attachFileRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    if (e) e.preventDefault();
    setSubmitting(true);

    try {
      const fullTranscript = liveTranscript.map(t => t.text).join(' ');
      const fullNotes = liveTranscript.length > 0 
        ? formData.notes + '\n\n--- Live Transcription ---\n' + liveTranscript.map(t => `[${t.timestamp}] ${t.text}`).join('\n')
        : formData.notes;

      const res = await axios.post(`${API}/lectures?auto_generate=${autoGenerate}`, {
        ...formData,
        notes: fullNotes,
        transcript: fullTranscript
      });
      const lectureId = res.data?.id;

      // Upload recorded audio blob if available
      if (recordedAudioBlob && lectureId) {
        try {
          const audioFormData = new FormData();
          const ext = MediaRecorder.isTypeSupported('audio/webm') ? 'webm' : 'mp4';
          audioFormData.append('file', recordedAudioBlob, `recording.${ext}`);
          await axios.post(`${API}/uploads/audio?lecture_id=${lectureId}`, audioFormData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });
        } catch (audioErr) {
          console.warn('Audio upload failed (lecture saved without audio):', audioErr);
        }
      }

      toast.success(autoGenerate 
        ? 'Lecture created! Flashcards generating in background...'
        : 'Lecture created with AI summary!'
      );
      // iter193 — subtle bell cue ties an emotion to the save moment.
      playSound('lecture-saved');
      // iter192 retention — surface any badges this action just unlocked
      // (e.g. "First Lecture", "Lecture Master 10"). Fire-and-forget so
      // failures here never block the rest of the save flow.
      checkAndCelebrate();
      setOpen(false);
      setLiveRecordingOpen(false);
      setFormData({ subject_id: '', title: '', topic: '', notes: '', transcript: '', is_public: false });
      setLiveTranscript([]);
      // Clean up audio state via the hook's consolidated reset
      if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
      resetRecordingState();
      fetchData();
    } catch (error) {
      console.error('Error creating lecture:', error);
      toast.error('Failed to create lecture');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this lecture?')) return;

    try {
      await axios.delete(`${API}/lectures/${id}`);
      toast.success('Lecture deleted');
      fetchData();
    } catch (error) {
      console.error('Error deleting lecture:', error);
      toast.error('Failed to delete lecture');
    }
  };

  // Export lecture to PDF - Full Study Package
  const handleExportPDF = async (lecture) => {
    const subject = subjects.find(s => s.id === lecture.subject_id);
    const toastId = toast.loading('Generating PDF...');
    
    try {
      const doc = new jsPDF();
      const pageWidth = doc.internal.pageSize.getWidth();
      const pageHeight = doc.internal.pageSize.getHeight();
      const margin = 20;
      const contentWidth = pageWidth - (margin * 2);
      let yPos = margin;
      
      // Helper function to add text with word wrapping
      const addWrappedText = (text, x, y, maxWidth, lineHeight = 6) => {
        const lines = doc.splitTextToSize(text, maxWidth);
        lines.forEach((line, i) => {
          if (y + (i * lineHeight) > pageHeight - margin) {
            doc.addPage();
            y = margin;
          }
          doc.text(line, x, y + (i * lineHeight));
        });
        return y + (lines.length * lineHeight);
      };
      
      // Header with gradient effect (simulated with rectangle)
      doc.setFillColor(139, 92, 246); // Purple
      doc.rect(0, 0, pageWidth, 45, 'F');
      
      // Title
      doc.setTextColor(255, 255, 255);
      doc.setFontSize(24);
      doc.setFont('helvetica', 'bold');
      doc.text('iStudyPro', margin, 20);
      
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text('Study Package', margin, 30);
      
      // Subject badge
      if (subject) {
        doc.setFontSize(10);
        doc.text(subject.name, pageWidth - margin - 30, 25);
      }
      
      yPos = 55;
      
      // Lecture Info
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(20);
      doc.setFont('helvetica', 'bold');
      yPos = addWrappedText(lecture.title, margin, yPos, contentWidth, 8);
      yPos += 5;
      
      if (lecture.topic) {
        doc.setFontSize(12);
        doc.setFont('helvetica', 'italic');
        doc.setTextColor(100, 100, 100);
        yPos = addWrappedText(lecture.topic, margin, yPos, contentWidth);
        yPos += 3;
      }
      
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.setTextColor(150, 150, 150);
      doc.text(`Date: ${format(new Date(lecture.date), 'PPP')}`, margin, yPos);
      yPos += 15;
      
      // AI Summary Section
      if (lecture.summary) {
        doc.setFillColor(243, 232, 255); // Light purple background
        doc.roundedRect(margin, yPos - 5, contentWidth, 10, 2, 2, 'F');
        
        doc.setTextColor(107, 33, 168); // Purple text
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('✨ AI Summary', margin + 5, yPos + 3);
        yPos += 15;
        
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        yPos = addWrappedText(lecture.summary, margin, yPos, contentWidth);
        yPos += 10;
      }
      
      // Key Points Section
      if (lecture.key_points && lecture.key_points.length > 0) {
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.setFillColor(254, 249, 195); // Light yellow background
        doc.roundedRect(margin, yPos - 5, contentWidth, 10, 2, 2, 'F');
        
        doc.setTextColor(161, 98, 7); // Yellow-brown text
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('⚡ Key Points', margin + 5, yPos + 3);
        yPos += 15;
        
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(11);
        doc.setFont('helvetica', 'normal');
        
        lecture.key_points.forEach((point, idx) => {
          if (yPos > pageHeight - 20) {
            doc.addPage();
            yPos = margin;
          }
          doc.text('•', margin, yPos);
          yPos = addWrappedText(point, margin + 8, yPos, contentWidth - 8);
          yPos += 3;
        });
        yPos += 7;
      }
      
      // Notes Section
      if (lecture.notes) {
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.setFillColor(219, 234, 254); // Light blue background
        doc.roundedRect(margin, yPos - 5, contentWidth, 10, 2, 2, 'F');
        
        doc.setTextColor(30, 64, 175); // Blue text
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('📝 Lecture Notes', margin + 5, yPos + 3);
        yPos += 15;
        
        doc.setTextColor(60, 60, 60);
        doc.setFontSize(10);
        doc.setFont('helvetica', 'normal');
        yPos = addWrappedText(lecture.notes, margin, yPos, contentWidth, 5);
        yPos += 10;
      }
      
      // Transcript Section
      if (lecture.transcript) {
        if (yPos > pageHeight - 60) {
          doc.addPage();
          yPos = margin;
        }
        
        doc.setFillColor(236, 253, 245); // Light green background
        doc.roundedRect(margin, yPos - 5, contentWidth, 10, 2, 2, 'F');
        
        doc.setTextColor(22, 101, 52); // Green text
        doc.setFontSize(14);
        doc.setFont('helvetica', 'bold');
        doc.text('🎤 Transcript', margin + 5, yPos + 3);
        yPos += 15;
        
        doc.setTextColor(80, 80, 80);
        doc.setFontSize(9);
        doc.setFont('helvetica', 'normal');
        yPos = addWrappedText(lecture.transcript, margin, yPos, contentWidth, 4.5);
      }
      
      // Footer with watermark
      const totalPages = doc.internal.getNumberOfPages();
      for (let i = 1; i <= totalPages; i++) {
        doc.setPage(i);
        doc.setFontSize(8);
        doc.setTextColor(150, 150, 150);

        // Watermarked line
        const lecturerLabel = subject?.lecturer_name ? `${subject.lecturer_name}'s` : '';
        const dateLabel = format(new Date(lecture.date), 'PPP');
        const watermark = lecturerLabel
          ? `Generated by istudyPRO from ${lecturerLabel} ${dateLabel} Lecture. For personal study use only.`
          : `Generated by istudyPRO \u2022 ${dateLabel} \u2022 For personal study use only.`;
        doc.text(watermark, pageWidth / 2, pageHeight - 14, { align: 'center' });

        doc.text(
          `Page ${i} of ${totalPages}`,
          pageWidth / 2,
          pageHeight - 8,
          { align: 'center' }
        );
      }
      
      // Save the PDF
      const fileName = `${lecture.title.replace(/[^a-z0-9]/gi, '_')}_study_package.pdf`;
      doc.save(fileName);
      
      toast.success('PDF exported successfully!', { id: toastId });
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF', { id: toastId });
    }
  };

  const startLiveRecording = () => {
    // Clean up previous recording assets via the hook's reset
    if (recordedAudioUrl) URL.revokeObjectURL(recordedAudioUrl);
    resetRecordingState();
    startRecording();
  };

  const handleStopRecording = async () => {
    // Capture audio blob before stopping (chunks will be cleared)
    const chunks = audioChunks.length > 0 ? audioChunks : [];
    
    const whisperText = await contextStopRecording();
    
    // Build audio blob for playback/download
    if (chunks.length > 0) {
      const mimeType = MediaRecorder.isTypeSupported('audio/webm') ? 'audio/webm' : 'audio/mp4';
      const blob = new Blob(chunks, { type: mimeType });
      const url = URL.createObjectURL(blob);
      setRecordedAudioBlob(blob);
      setRecordedAudioUrl(url);
    }
    
    // Set transcript text
    if (whisperText) {
      setFormData(prev => ({ ...prev, transcript: whisperText }));
      setFinalTranscriptText(whisperText);
    } else if (liveTranscript.length > 0) {
      const combinedText = liveTranscript.map(t => t.text).join(' ');
      setFinalTranscriptText(combinedText);
    }
  };

  // Download transcript as text file
  const downloadTranscript = () => {
    if (!finalTranscriptText) return;
    const blob = new Blob([finalTranscriptText], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `transcript_${new Date().toISOString().slice(0,10)}.txt`;
    a.click();
    URL.revokeObjectURL(url);
  };

  // Download recorded audio
  const downloadAudio = () => {
    if (!recordedAudioUrl) return;
    const ext = MediaRecorder.isTypeSupported('audio/webm') ? 'webm' : 'mp4';
    const a = document.createElement('a');
    a.href = recordedAudioUrl;
    a.download = `recording_${new Date().toISOString().slice(0,10)}.${ext}`;
    a.click();
  };

  // "I'm Confused" Button Handlers
  const handleConfusedButtonClick = async (mode = 'explain') => {
    if (mode === 'mark') {
      markConfused('mark');
      return;
    }
    
    // Pause recognition while showing explanation
    pauseSpeechRecognition();
    
    setConfusedPopupOpen(true);
    setConfusedLoading(true);
    setConfusedExplanation('');
    
    const recentTranscript = liveTranscript.slice(-3).map(t => t.text).join(' ') || 
                             interimText || 
                             'the current topic being discussed';
    
    try {
      const response = await axios.post(`${API}/simplify-text`, {
        transcript_snippet: recentTranscript.slice(-500),
        timestamp: formatTime(recordingTime)
      });
      setConfusedExplanation(response.data.simplified || 'No explanation available');
    } catch (error) {
      console.error('Error getting explanation:', error);
      setConfusedExplanation('Sorry, I couldn\'t generate an explanation right now. Try marking this moment for later review.');
    } finally {
      setConfusedLoading(false);
    }
  };

  const closeConfusedPopup = () => {
    setConfusedPopupOpen(false);
    setConfusedExplanation('');
    resumeSpeechRecognition();
  };

  const handleGenerateLearningBytes = async (lectureId) => {
    try {
      toast.info('Generating learning bytes with AI...');
      const response = await axios.post(`${API}/learning-bytes/generate/${lectureId}`);
      const fc = response.data.flashcards_created || 0;
      const qz = response.data.quizzes_created || 0;
      toast.success(`Created ${fc} flashcards and ${qz} quizzes!`);
    } catch (error) {
      console.error('Error generating bytes:', error);
      toast.error(error.response?.data?.detail || 'Failed to generate learning bytes');
    }
  };

  const handleShare = (lecture) => {
    setShareLecture(lecture);
    setShareOpen(true);
  };

  const handleShareStudyKit = async (lecture) => {
    try {
      toast.info('Creating shareable study kit link...');
      const res = await axios.post(`${API}/share/create`, {
        lecture_id: lecture.id,
        include_flashcards: true,
        include_quizzes: true,
        include_summary: true,
      });
      const ogBase = process.env.REACT_APP_BACKEND_URL || window.location.origin;
      const shareUrl = `${ogBase}/api/og/share/${res.data.share_id}`;
      await navigator.clipboard.writeText(shareUrl);
      toast.success(
        res.data.already_exists
          ? 'Share link copied! (link already existed)'
          : `Study kit created! ${res.data.flashcards_count} flashcards, ${res.data.quizzes_count} quizzes. Link copied!`
      );
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create share link');
    }
  };

  const filteredLectures = selectedSubject === 'all' 
    ? lectures 
    : lectures.filter(l => l.subject_id === selectedSubject);

  if (loading) {
    return (
      <div className="space-y-6" data-testid="lectures-loading">
        <PageSkeleton variant="grid" rows={6} />
      </div>
    );
  }

  return (
    <div className="space-y-6 sm:space-y-8" data-testid="lectures-page">
      <ShareLectureDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        lecture={shareLecture}
      />

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-start gap-4">
        <div>
          {/* Breadcrumb when filtering by subject */}
          {selectedSubject !== 'all' && (() => {
            const activeSubject = subjects.find(s => s.id === selectedSubject);
            return activeSubject ? (
              <div className="flex items-center gap-2 mb-2 text-sm" data-testid="subject-breadcrumb">
                <Link to="/subjects" className="text-[#002FA7] hover:underline font-medium">Subjects</Link>
                <span className="text-[var(--isp-muted)]">/</span>
                <span className="text-[var(--isp-heading)] font-medium flex items-center gap-1.5">
                  <span className="w-2.5 h-2.5 rounded-full inline-block" style={{ backgroundColor: activeSubject.color }} />
                  {activeSubject.name}
                </span>
                <button onClick={() => { setSelectedSubject('all'); setSearchParams({}); }} className="text-[10px] text-[var(--isp-muted)] hover:text-red-400 ml-1">&times;</button>
              </div>
            ) : null;
          })()}
          <h1 className="font-serif-accent text-3xl sm:text-4xl md:text-5xl font-bold text-[var(--isp-heading)] tracking-tight">
            Lectures
          </h1>
          <p className="text-[var(--isp-body)] mt-2 text-base sm:text-lg">
            Record, transcribe, and master your lectures with AI
          </p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Tip text="Record a live lecture using your microphone">
            <Button 
              onClick={() => setLiveRecordingOpen(true)}
              className="gap-2 isp-btn-primary rounded-xl shadow-lg shadow-[var(--isp-glow-green)]"
              data-testid="live-record-button"
            >
              <Mic className="w-4 h-4" />
              Live Record
            </Button>
          </Tip>
          <Tip text="Capture audio from a browser tab (online lectures)">
            <Button
              onClick={async () => { await startTabCapture(); setLiveRecordingOpen(true); }}
              variant="outline"
              className="gap-2 rounded-xl border-blue-500/30 text-blue-300 hover:text-blue-200 hover:bg-blue-500/10"
              data-testid="capture-tab-button"
            >
              <Radio className="w-4 h-4" />
              Capture Tab
            </Button>
          </Tip>
          <Tip text="Upload PDF, DOCX, or text files for AI analysis">
            <div className="relative">
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={docUploading}
                variant="outline"
                className="gap-2 rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]"
                data-testid="upload-document-button"
              >
                {docUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}
                {docUploading ? `${uploadProgress}%` : 'Upload Doc'}
              </Button>
              {docUploading && uploadProgress > 0 && (
                <div className="absolute bottom-0 left-0 right-0 h-0.5 rounded-full bg-[var(--isp-glass-border)] overflow-hidden">
                  <div className="h-full bg-gradient-to-r from-blue-500 to-cyan-500 transition-all duration-300" style={{ width: `${uploadProgress}%` }} />
                </div>
              )}
            </div>
          </Tip>
          <input
            ref={fileInputRef}
            type="file"
            accept=".pdf,.txt,.md,.csv,.doc,.docx"
            onChange={handleDocumentUpload}
            className="hidden"
            data-testid="doc-file-input"
          />
          <Link to="/document-uploads">
            <Button
              variant="outline"
              className="gap-2 rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[#002FA7] hover:bg-[#002FA7]/5"
              data-testid="document-uploads-link"
            >
              <FileText className="w-4 h-4" />
              My Documents
            </Button>
          </Link>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button 
                className="gap-2 isp-btn-coral rounded-xl shadow-lg"
                data-testid="create-lecture-button"
              >
                <Plus className="w-4 h-4" />
                New Lecture
              </Button>
            </DialogTrigger>
          <Button 
            variant="outline"
            className="gap-2 rounded-xl border-[var(--isp-glass-border)] text-[var(--isp-heading)] hover:bg-violet-600/10"
            onClick={() => setImportOpen(true)}
            data-testid="import-lecture-button"
          >
            <Upload className="w-4 h-4" />
            Import
          </Button>
            <CreateLectureForm
              formData={formData}
              setFormData={setFormData}
              subjects={subjects}
              setSubjects={setSubjects}
              autoGenerate={autoGenerate}
              setAutoGenerate={setAutoGenerate}
              submitting={submitting}
              onSubmit={handleSubmit}
              onCancel={() => setOpen(false)}
            />
          </Dialog>
          {/* Audio Upload → transcribe-only flow. Lives OUTSIDE the
              New-Lecture Dialog above so its own dialog isn't nested. */}
          <AudioUploadDialog
            subjects={subjects}
            onLectureCreated={(lec) => setLectures((prev) => [lec, ...prev])}
          />
        </div>
      </div>

      {/* Live Recording Modal */}
      <LiveRecordingDialog s={{
        // recording context (from useRecording)
        recording, transcribing, recordingTime, liveTranscript,
        interimText, speechSupported, isListening, confusedMarkers,
        bookmarks, lastVoiceCommand, audioChunks, recordingSource,
        formatTime, addBookmark, markConfused,
        // transcription context (from useTranscription)
        transcriptionMode, setTranscriptionMode, localModelReady,
        localModelLoading, localModelProgress, localModelFailed,
        initLocalWhisper,
        // dialog-local state + refs
        recordedAudioUrl, recordedAudioBlob, finalTranscriptText,
        setFinalTranscriptText, attachedFiles, setAttachedFiles,
        attachUploading, attachFileRef, titleSuggestions,
        setTitleSuggestions, suggestingTitle, confusedPopupOpen,
        confusedExplanation, confusedLoading,
        formData, setFormData, autoGenerate, setAutoGenerate,
        submitting, subjects, setSubjects, transcriptContainerRef,
        liveRecordingOpen, setLiveRecordingOpen,
        // handlers
        minimizeRecording, handleStopRecording, startLiveRecording,
        startTabCapture, handleConfusedButtonClick, closeConfusedPopup,
        downloadAudio, downloadTranscript, suggestTitles,
        handleAttachFile, handleSubmit, handleRecordingTouchStart,
        handleRecordingTouchEnd,
      }} />

      {/* Subject filter */}
      {subjects.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-2">
          <Button
            variant={selectedSubject === 'all' ? 'default' : 'outline'}
            size="sm"
            onClick={() => { setSelectedSubject('all'); setSearchParams({}); }}
            className={`rounded-xl whitespace-nowrap ${
              selectedSubject === 'all' 
                ? 'bg-[#002FA7]/20 text-[#002FA7] border border-[#002FA7]/30' 
                : 'border-[var(--isp-glass-border)] text-[var(--isp-body)] hover:text-[var(--isp-heading)] hover:bg-[var(--isp-glass-inner)]'
            }`}
          >
            All Subjects
          </Button>
          {subjects.map((subject) => (
            <Button
              key={subject.id}
              variant={selectedSubject === subject.id ? 'default' : 'outline'}
              size="sm"
              onClick={() => { setSelectedSubject(subject.id); setSearchParams({ subject: subject.id }); }}
              className="rounded-xl whitespace-nowrap"
              style={selectedSubject === subject.id 
                ? { backgroundColor: subject.color, color: 'white' } 
                : { borderColor: 'rgba(255,255,255,0.1)', color: 'rgba(255,255,255,0.7)' }
              }
            >
              {subject.name}
            </Button>
          ))}
        </div>
      )}

      {/* Lectures grid */}
      <div className="grid grid-cols-1 gap-4 sm:gap-6">
        {filteredLectures.length === 0 ? (
          <div className="p-12 isp-card text-center">
            <img
              src="https://static.prod-images.emergentagent.com/jobs/8d1c3d2f-8f11-4b99-a134-086589108ffc/images/45ce583b5af039f9c4a42fc2498b8890f30e3327594784789af8727cba09965a.png"
              alt="Empty desk ready for study"
              className="w-40 h-40 mx-auto mb-4  object-cover opacity-80"
              loading="lazy"
            />
            <h3 className="text-xl font-medium text-[var(--isp-heading)] mb-2">No lectures yet</h3>
            <p className="text-[var(--isp-body)] mb-6">Record your first lecture to get started with AI-powered learning</p>
            <Button 
              onClick={() => setLiveRecordingOpen(true)} 
              className="rounded-xl gap-2 isp-btn-primary"
              data-testid="empty-record-lecture"
            >
              <Mic className="w-4 h-4" />
              Start Recording
            </Button>
          </div>
        ) : (
          filteredLectures.map((lecture, index) => {
            const subject = subjects.find(s => s.id === lecture.subject_id);
            return (
              <LectureCard
                key={lecture.id}
                lecture={lecture}
                index={index}
                subject={subject}
                isSelected={selectedLecture?.id === lecture.id}
                onToggleSelect={() => setSelectedLecture(selectedLecture?.id === lecture.id ? null : lecture)}
                onDelete={handleDelete}
                onExportPDF={handleExportPDF}
                onGenerateLearningBytes={handleGenerateLearningBytes}
                onShare={handleShare}
                onShareStudyKit={handleShareStudyKit}
              />
            );
          })
        )}
      </div>

      {/* Import Modal */}
      <ImportModal
        open={importOpen}
        onOpenChange={setImportOpen}
        subjects={subjects}
        onSuccess={() => fetchData()}
      />
    </div>
  );
};

export default LecturesPage;
